/* Locations — admin page to manage per-location data isolation.
 * Create/rename/delete locations, see how much data lives in each.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin,
  MapPinned,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  Users as UsersIcon,
  Building2,
  ContactRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {} (new) | {id, name} (rename)
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations", { params: { with_counts: true } });
      setLocations(data.locations || []);
    } catch (e) {
      toast.error("Failed to load locations", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (loc) => {
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success(`Deleted "${loc.name}"`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Delete blocked", { description: formatApiError(e) });
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="locations-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Admin</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
            Locations
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Each location is an isolated data workspace. Assign users to locations on the
            Users page — they'll only see realtors and brokerages for their location(s).
          </p>
        </div>
        <Button
          onClick={() => setEditor({})}
          className="text-white min-h-[44px]"
          style={{ background: "hsl(var(--brand))" }}
          data-testid="add-location-button"
        >
          <Plus className="h-4 w-4 mr-2" /> Add location
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12 text-sm text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : locations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
          <MapPin className="h-6 w-6 mx-auto text-slate-300 mb-2" />
          No locations yet. Add your first one — e.g. "Saskatoon, SK".
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {locations.map((loc) => (
            <div
              key={loc.id}
              className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-3"
              data-testid={`location-card-${loc.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">{loc.name}</div>
                    <div className="text-[11px] text-slate-400">
                      Added {loc.created_at ? new Date(loc.created_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditor({ id: loc.id, name: loc.name })}
                    title="Rename"
                    data-testid={`rename-location-${loc.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDelete(loc)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    title="Delete"
                    data-testid={`delete-location-${loc.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat icon={ContactRound} label="Realtors" value={loc.realtor_count ?? 0} />
                <Stat icon={Building2} label="Brokerages" value={loc.brokerage_count ?? 0} />
                <Stat icon={UsersIcon} label="Users" value={loc.user_count ?? 0} />
              </div>
              {loc.territory_key ? (
                <Link
                  to={`/territory?key=${loc.territory_key}`}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors min-h-[40px]"
                  data-testid={`open-territory-${loc.id}`}
                >
                  <MapPinned className="h-3.5 w-3.5" /> Open territory map & town import
                </Link>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <LocationEditorDialog
        editor={editor}
        onClose={() => setEditor(null)}
        onSaved={() => { setEditor(null); load(); }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Delete "${confirmDelete?.name}"?`}
        description="Only empty locations can be deleted. Users assigned to it will lose the assignment."
        confirmLabel="Delete location"
        destructive
        onConfirm={() => remove(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-100 py-2">
      <div className="flex items-center justify-center gap-1 text-slate-400">
        <Icon className="h-3 w-3" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-base font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function LocationEditorDialog({ editor, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const isRename = !!editor?.id;

  useEffect(() => {
    setName(editor?.name || "");
    setBusy(false);
  }, [editor]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (isRename) {
        await api.patch(`/locations/${editor.id}`, { name: name.trim() });
        toast.success("Location renamed");
      } else {
        await api.post("/locations", { name: name.trim() });
        toast.success("Location created");
      }
      onSaved();
    } catch (err) {
      toast.error(isRename ? "Rename failed" : "Create failed", { description: formatApiError(err) });
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!editor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="location-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isRename ? "Rename location" : "Add a location"}</DialogTitle>
          <DialogDescription>
            Use "City, Province" format — it doubles as the search area for scrapes
            tagged to this location.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Edmonton, AB"
              required
              className="mt-1 min-h-[44px]"
              data-testid="location-name-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !name.trim()}
              className="text-white min-h-[44px]"
              style={{ background: "hsl(var(--brand))" }}
              data-testid="location-save-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isRename ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
